import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CreditCard } from "lucide-react";
import { useEffect } from "react";
import { AppHeader } from "../../../components/app-header";
import { Button } from "../../../components/ui/button";
import { Banner, InlineError, Skeleton } from "../../../components/ui/feedback";
import { notify } from "../../../components/ui/toast";
import { account } from "../../../copy/home";
import { api, type CheckoutMode, errorMessage } from "../../../lib/api";
import { useDelayedFlag } from "../../../lib/hooks";
import { useOnline } from "../../../lib/online";
import { billingQuery } from "../../../lib/queries";

type Checkout = "success" | "cancelled";

export const Route = createFileRoute("/_authed/account/billing")({
  validateSearch: (search: Record<string, unknown>): { checkout?: Checkout } => {
    const c = search.checkout;
    return c === "success" || c === "cancelled" ? { checkout: c } : {};
  },
  component: BillingPage,
});

/** Billing (UX_SPEC §11). Card data never touches Carno — Stripe Checkout/Portal only. */
function BillingPage() {
  const online = useOnline();
  const { checkout } = Route.useSearch();
  const navigate = Route.useNavigate();
  const billing = useQuery(billingQuery);
  const showSkeleton = useDelayedFlag(billing.isPending, 150);
  const copy = account.billingPage;

  // Surface the ?checkout= result once, then drop it from the URL.
  useEffect(() => {
    if (!checkout) return;
    if (checkout === "success") notify.success(copy.checkoutSuccess);
    else notify.info(copy.checkoutCancelled);
    void navigate({ search: {}, replace: true });
  }, [checkout, copy.checkoutSuccess, copy.checkoutCancelled, navigate]);

  const checkoutMut = useMutation({
    mutationFn: (mode: CheckoutMode) => api.startCheckout(mode),
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (e) => notify.error(errorMessage(e, copy.actionFailed)),
  });
  const portalMut = useMutation({
    mutationFn: () => api.openBillingPortal(),
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (e) => notify.error(errorMessage(e, copy.actionFailed)),
  });
  const busy = checkoutMut.isPending || portalMut.isPending;

  return (
    <div className="min-h-dvh bg-background">
      <AppHeader />
      <main id="main" className="mx-auto max-w-2xl px-4 pt-8 pb-16 sm:px-6">
        <h1 className="text-2xl font-semibold">{copy.pageTitle}</h1>
        <p className="mt-1 mb-6 text-sm text-muted-foreground">{copy.intro}</p>
        {billing.isPending ? (
          showSkeleton ? (
            <Skeleton className="h-40 w-full" />
          ) : null
        ) : billing.isError ? (
          <InlineError message={copy.loadFailed} onRetry={() => void billing.refetch()} />
        ) : !billing.data.billingEnabled ? (
          <p className="rounded-xl border border-border bg-surface px-4 py-6 text-center text-sm text-muted-foreground">{copy.notEnabled}</p>
        ) : (
          <div className="flex flex-col gap-6">
            {billing.data.delinquent && <Banner tone="warn">{copy.delinquent}</Banner>}

            <section aria-labelledby="plan-h" className="rounded-xl border border-border px-4 py-4">
              <h2 id="plan-h" className="text-sm font-medium text-muted-foreground">
                {copy.planLabel}
              </h2>
              <p className="mt-1 text-lg font-semibold">{billing.data.plan === "pro" ? copy.plans.pro : copy.plans.free}</p>
            </section>

            <section aria-labelledby="pm-h" className="rounded-xl border border-border px-4 py-4">
              <h2 id="pm-h" className="text-sm font-medium text-muted-foreground">
                {copy.paymentMethod}
              </h2>
              <p className="mt-1 flex items-center gap-2 text-sm">
                <CreditCard className="size-4 text-muted-foreground" aria-hidden="true" />
                {billing.data.hasPaymentMethod && billing.data.cardBrand && billing.data.cardLast4
                  ? copy.cardOnFile(billing.data.cardBrand, billing.data.cardLast4)
                  : copy.noCard}
              </p>
            </section>

            <div className="flex flex-wrap gap-3">
              <Button variant="primary" disabled={!online || busy} onClick={() => checkoutMut.mutate("setup")}>
                {copy.addCard}
              </Button>
              {billing.data.plan !== "pro" && (
                <Button variant="secondary" disabled={!online || busy} onClick={() => checkoutMut.mutate("subscription")}>
                  {copy.upgrade}
                </Button>
              )}
              {billing.data.hasCustomer && (
                <Button variant="secondary" disabled={!online || busy} onClick={() => portalMut.mutate()}>
                  {copy.manage}
                </Button>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
